import { sv } from 'date-fns/locale';
import React, { useCallback, useContext, useEffect, useReducer, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { PerspectiveCamera } from 'three';

import { FeatureFlagsManager } from '../../../../../../utils/core/feature-flags-manager';
import { formattedDate } from '../../../../../../utils/dateUtils';
import { getMetersPerUnit, LinearUnit } from '../../../../../../utils/proj4/units';
import { normalizeString } from '../../../../../../utils/text/text';
import cameraManager from '../../../../../../viewer/camera-manager';
import { publish, SubscriptionTopic } from '../../../../../../viewer/messaging/pubsub';
import {
  getLegacyDrawingsInView,
  getOmittedLayersInView,
  isOrthophotoInView,
  ReportRegion,
} from '../../../../../../viewer/report/collect-report-layers';
import { SceneManager } from '../../../../../../viewer/scene-manager';
import { ReportTool } from '../../../../../../viewer/tools/report-tool';
import { ReportFrameState } from '../../../../../../viewer/tools/utils/report-frame';
import {
  degreesToRadians,
  radiansToDegrees,
} from '../../../../../../viewer/utilities/math-helpers';
import { ViewSettings } from '../../../../../../viewer/view-settings';
import { useProjectSnapshot } from '../../../../../hooks/use-project-snapshot';
import { useSubscribe } from '../../../../../hooks/use-subscribe';
import { ProjectSpatialReferenceContext } from '../../../../../state/project-reference-state';
import { useOptionalUserState } from '../../../../../state/user-state/user-state';
import { Button } from '../../../../button/button';
import { CheckBox } from '../../../../check-box/check-box';
import { InfoBox } from '../../../../info-box/info-box';
import { LabelledContainer } from '../../../../labelled-container/labelled-container';
import { Select } from '../../../../select/select';
import {
  RightPanelContent,
  RightPanelFooter,
} from '../../../../skyview/right-menu/components/styles';
import { SkyviewContext } from '../../../../skyview/skyview-state';
import { Slider } from '../../../../slider/slider';
import { Stack } from '../../../../stack/stack';
import { TextBox } from '../../../../text-box/text-box';
import { ToggleGroup } from '../../../../toggle-group/toggle-group';
import { getMapAreaMm, PageOrientation, PaperSize, paperSizes } from '../helpers/types';
import { CadDrawingReportExportDialog } from './cad-drawing-report-export-dialog';
import { CadDrawingReportOmitted } from './cad-drawing-report-omitted';
import { CadDrawingTitleBlockFields } from './cad-drawing-title-block-fields';
import { CameraViewCrop } from './camera-view-crop';
import {
  defaultPageSettings,
  formatScale,
  getFittingScale,
  getFrameSize,
  getPresetScales,
  PageSettings,
  scaleFromInput,
  scaleToInput,
} from './page-settings';
import {
  cadDrawingReportExport,
  downloadReportPayload,
  isExportRunning,
  ReportContent,
} from './report-export';
import {
  buildCameraPayload,
  buildPlanPayload,
  buildScreenshotPayload,
  CadDrawingReportOptions,
  ReportDpi,
  reportDpis,
} from './report-payload';

function optionsReducer(
  state: CadDrawingReportOptions,
  values: Partial<CadDrawingReportOptions>,
): CadDrawingReportOptions {
  return { ...state, ...values };
}

const CUSTOM_SCALE = 'custom';

/** Frame sizes closer than this, as a part of the size, are the same. */
const SIZE_TOLERANCE = 1e-9;

type Props = {
  /** The page, kept by the report menu so it stays when the user switches report type. */
  page: PageSettings | undefined;
  onPageChange: (page: PageSettings) => void;
};

/**
 * The CAD drawing report rendered by the backend (SK-7544): the user picks the page and its scale,
 * and places its frame in the top-down view.
 */
export const CadDrawingReportMenu = ({ page, onPageChange }: Props) => {
  const { t, i18n } = useTranslation();
  const { project } = useProjectSnapshot();
  const { skyview, activeTool } = useContext(SkyviewContext);
  const tool = activeTool instanceof ReportTool ? activeTool : undefined;

  // The context has no default value, so it is undefined outside ProjectReferenceProvider.
  const spatialReference = useContext(ProjectSpatialReferenceContext);
  const unit: LinearUnit = spatialReference?.linearUnit ?? 'm';
  const coordinateSystem = spatialReference?.coordinateSystem;
  const metersPerUnit = getMetersPerUnit(coordinateSystem?.proj4 ?? coordinateSystem?.wkt);

  const subscribeToFrame = useCallback(
    (listener: () => void) => tool?.subscribeToFrame(listener) ?? (() => undefined),
    [tool],
  );
  const frame = React.useSyncExternalStore(subscribeToFrame, () => tool?.getFrame());

  const [isTopDownView, setIsTopDownView] = useState(skyview.isTopDownViewActive());
  // The backend renders the 3D view through the camera; a panorama's 360° images are a screenshot.
  const [isPanorama, setIsPanorama] = useState(
    SceneManager.instance.getActiveSceneType() === 'panoramaViewer',
  );
  const refreshView = useCallback(() => {
    setIsTopDownView(skyview.isTopDownViewActive());
    setIsPanorama(SceneManager.instance.getActiveSceneType() === 'panoramaViewer');
  }, [skyview]);
  /** What the report shows: the frame from above, or through the camera in 3D. */
  const getRegion = (): ReportRegion | undefined => {
    if (isTopDownView) {
      return frame ? { mode: 'plan', frame } : undefined;
    }
    const camera = cameraManager().getActiveCamera()?.getTHREECamera();
    return !isPanorama && camera instanceof PerspectiveCamera
      ? { mode: 'camera', camera }
      : undefined;
  };
  // The 3D view the backend renders, with the options it draws with, as the plan
  const isCameraView = !isTopDownView && !isPanorama;
  useSubscribe(SubscriptionTopic.OnSceneChangeCompleted, refreshView);
  useSubscribe(SubscriptionTopic.OnCameraChanged, refreshView);

  // What is shown but left out, refreshed when the frame, the assets or the view settings change.
  const [assetsChanged, setAssetsChanged] = useState(0);
  const countAssetChange = useCallback(() => setAssetsChanged((count) => count + 1), []);
  useSubscribe(SubscriptionTopic.AssetChanged, countAssetChange);
  useSubscribe(SubscriptionTopic.AssetListChanged, countAssetChange);
  const viewSettings = React.useSyncExternalStore(
    ViewSettings.instance.subscribe,
    ViewSettings.instance.getSnapshot,
  );
  const omitted = React.useMemo(
    () => {
      const region = getRegion();
      return region ? getOmittedLayersInView(skyview, region) : [];
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps -- recomputed when they change
    [skyview, isTopDownView, isPanorama, frame, assetsChanged, viewSettings],
  );

  const userState = useOptionalUserState();
  const resolvedUserName = userState?.getName() ?? '';
  const [options, dispatchOptions] = useReducer(optionsReducer, {
    includeDrawingFrame: true,
    useWhiteBackground: true,
    useBlackLinesAndText: false,
    lineThickness: 1,
    dpi: 200,
    includeOrthophoto: true,
    title: t('cadDrawingReport.pdf.rightPanel.titleDefault', { ns: 'skyviewReport' }),
    description: '',
    responsible: resolvedUserName,
    freeText: '',
  });

  useEffect(() => {
    if (options.responsible === '' && resolvedUserName !== '') {
      dispatchOptions({ responsible: resolvedUserName });
    }
  }, [resolvedUserName, options.responsible]);

  useEffect(() => {
    tool?.showFrame(isTopDownView);
    return () => tool?.showFrame(false);
  }, [tool, isTopDownView]);

  // The first time: in the top-down view, a page at a scale whose frame fits in the view; in the 3D
  // view, which has no scale, a page at a middle scale for when the user switches.
  useEffect(() => {
    if (page) {
      return;
    }

    const view = isTopDownView ? tool?.getView() : undefined;
    if (!view) {
      const scales = getPresetScales(unit);
      onPageChange({ ...defaultPageSettings, scale: scales[Math.floor(scales.length / 2)] });
      return;
    }

    const scale = getFittingScale(defaultPageSettings, view, unit, metersPerUnit);
    const newPage = { ...defaultPageSettings, scale };
    onPageChange(newPage);
    tool?.setFrame({ center: view.center, rotation: 0, ...getFrameSize(newPage, metersPerUnit) });
  }, [page, tool, isTopDownView, unit, metersPerUnit, onPageChange]);

  // A frame in the middle of the view, when the page was made in the 3D view.
  useEffect(() => {
    const view = tool?.getView();
    if (!page || frame || !tool || !isTopDownView || !view) {
      return;
    }

    tool.setFrame({ center: view.center, rotation: 0, ...getFrameSize(page, metersPerUnit) });
  }, [page, frame, tool, isTopDownView, metersPerUnit]);

  // Keeps the frame the page's size, e.g. when the coordinate system is loaded after the page.
  useEffect(() => {
    if (!tool || !page || !frame) {
      return;
    }

    const size = getFrameSize(page, metersPerUnit);
    if (!isSameSize(frame, size)) {
      tool.setFrame({ ...frame, ...size });
    }
  }, [tool, page, frame, metersPerUnit]);

  /** Resizes the frame about its center, and zooms the view to it to show what the scale means. */
  const changePage = (values: Partial<PageSettings>) => {
    if (!page) {
      return;
    }

    const newPage = { ...page, ...values };
    if (tool && frame) {
      tool.setFrame({ ...frame, ...getFrameSize(newPage, metersPerUnit) });
      tool.fitViewToFrame();
    }
    onPageChange(newPage);
  };

  const [saveResult, setSaveResult] = useState(false);
  const exportState = React.useSyncExternalStore(
    cadDrawingReportExport.subscribe,
    cadDrawingReportExport.getState,
  );
  const isExporting = isExportRunning(exportState);
  const [showExportDialog, setShowExportDialog] = useState(false);
  const proj4 = coordinateSystem?.proj4;
  // From the 3D view, the report is the view, without a frame.
  const canExport = (!isTopDownView || !!frame) && !!page && !!project && !!proj4 && !isExporting;

  /**
   * What the report is made of: a drawing to scale of the frame, the 3D view through the camera,
   * or a screenshot of a panorama.
   */
  const getReportContent = (): ReportContent | undefined => {
    if (!canExport) {
      return undefined;
    }

    const input = {
      page,
      frame,
      options,
      projectId: project.id,
      projectName: project.name,
      crs: { proj4, unit, metersPerUnit },
      locale: i18n.language,
      labels: {
        title: t('cadDrawingReport.pdf.rightPanel.title', { ns: 'skyviewReport' }),
        projectName: t('cadDrawingReport.pdf.rightPanel.projectName', { ns: 'skyviewReport' }),
        description: t('cadDrawingReport.pdf.rightPanel.description', { ns: 'skyviewReport' }),
        scale: t('cadDrawingReport.page.scale', { ns: 'skyviewReport' }),
        date: t('cadDrawingReport.pdf.rightPanel.date', { ns: 'skyviewReport' }),
        responsible: t('cadDrawingReport.pdf.rightPanel.responsible', { ns: 'skyviewReport' }),
      },
      date: new Date(),
    };

    const region = getRegion();
    if (region?.mode === 'plan') {
      const { frame: planFrame } = region;
      return {
        drawings: getLegacyDrawingsInView(region),
        buildPayload: (unconvertedVersionIds) =>
          buildPlanPayload(skyview, { ...input, frame: planFrame, unconvertedVersionIds }),
      };
    }
    if (region?.mode === 'camera') {
      return {
        drawings: getLegacyDrawingsInView(region),
        buildPayload: (unconvertedVersionIds) =>
          buildCameraPayload(skyview, { ...input, unconvertedVersionIds }),
      };
    }
    return { drawings: [], buildPayload: () => buildScreenshotPayload(skyview, input) };
  };

  const exportReport = async () => {
    const content = getReportContent();
    if (!content || !project) {
      return;
    }

    setShowExportDialog(true);
    await cadDrawingReportExport.start(content, {
      projectId: project.id,
      fileName: normalizeString(`${project.name}_${formattedDate(Date.now(), sv)}.pdf`),
      saveResult,
    });
  };

  const downloadPayload = async () => {
    const content = getReportContent();
    if (!content) {
      return;
    }

    try {
      await downloadReportPayload(content);
    } catch (error) {
      console.error('Failed to download the report payload:', error);
      publish(SubscriptionTopic.ToastrMessage, {
        message: t('exportError', { ns: 'skyviewReport' }),
        type: 'error',
      });
    }
  };

  const switchToTopDownView = () => {
    if (ViewSettings.instance.isFlexible) {
      ViewSettings.instance.setCamera('orthographic');
    } else {
      ViewSettings.instance.showClassicView('orthophoto');
    }
  };
  const canSwitchToTopDownView =
    skyview.isTimelineViewActive() &&
    (ViewSettings.instance.isFlexible || skyview.isOrthoSceneAvailable());

  return (
    <>
      <RightPanelContent useTopPadding={false}>
        <Stack spacing={1}>
          {!isTopDownView && (
            <InfoBox color={isCameraView ? 'blue' : 'yellow'}>
              <Stack spacing={0.5}>
                {isCameraView && (
                  <span>{t('cadDrawingReport.cameraViewInfo', { ns: 'skyviewReport' })}</span>
                )}
                <span>{t('cadDrawingReport.topDownViewRequired', { ns: 'skyviewReport' })}</span>
                {!isCameraView && (
                  <span>{t('cadDrawingReport.screenshotInfo', { ns: 'skyviewReport' })}</span>
                )}
                {canSwitchToTopDownView && (
                  <Button color="secondary" variant="contained" onClick={switchToTopDownView}>
                    {t('cadDrawingReport.switchToTopDownView', { ns: 'skyviewReport' })}
                  </Button>
                )}
              </Stack>
            </InfoBox>
          )}

          {page && (!isTopDownView || frame) && (
            <PageSection
              frame={isTopDownView ? frame : undefined}
              isCameraView={isCameraView}
              page={page}
              tool={tool}
              unit={unit}
              onPageChange={changePage}
            />
          )}

          <CheckBox
            checked={options.useWhiteBackground}
            onChange={() => dispatchOptions({ useWhiteBackground: !options.useWhiteBackground })}
          >
            {t('cadDrawingReport.useWhiteBackground', { ns: 'skyviewReport' })}
          </CheckBox>

          <CheckBox
            checked={options.includeDrawingFrame}
            onChange={() => dispatchOptions({ includeDrawingFrame: !options.includeDrawingFrame })}
          >
            {t('cadDrawingReport.includeDrawingFrame', { ns: 'skyviewReport' })}
          </CheckBox>

          {(isTopDownView || isCameraView) && isOrthophotoInView(skyview, isCameraView) && (
            <CheckBox
              checked={options.includeOrthophoto}
              onChange={() => dispatchOptions({ includeOrthophoto: !options.includeOrthophoto })}
            >
              {t('cadDrawingReport.includeOrthophoto', { ns: 'skyviewReport' })}
            </CheckBox>
          )}

          {(isTopDownView || isCameraView) && (
            <CheckBox
              checked={options.useBlackLinesAndText}
              onChange={() =>
                dispatchOptions({ useBlackLinesAndText: !options.useBlackLinesAndText })
              }
            >
              {t('cadDrawingReport.useBlackLinesAndText', { ns: 'skyviewReport' })}
            </CheckBox>
          )}

          {(isTopDownView || isCameraView) && (
            <LabelledContainer text={t('cadDrawingReport.lineThickness', { ns: 'skyviewReport' })}>
              {() => (
                <Slider
                  max={1.5}
                  min={0.05}
                  step={0.05}
                  value={options.lineThickness}
                  onChange={(value) => dispatchOptions({ lineThickness: value })}
                />
              )}
            </LabelledContainer>
          )}

          {(isTopDownView || isCameraView) && (
            <LabelledContainer
              text={t('cadDrawingReport.imageResolution', { ns: 'skyviewReport' })}
            >
              <ToggleGroup<ReportDpi>
                items={reportDpis.map((dpi) => ({ value: dpi, label: `${dpi} dpi` }))}
                value={options.dpi}
                onChange={(dpi) => dispatchOptions({ dpi })}
              />
            </LabelledContainer>
          )}

          <CadDrawingReportOmitted omitted={omitted} />

          {options.includeDrawingFrame && (
            <CadDrawingTitleBlockFields values={options} onChange={dispatchOptions} />
          )}

          <CheckBox checked={saveResult} onChange={() => setSaveResult(!saveResult)}>
            {t('cadDrawingReport.export.saveToFileManager', { ns: 'skyviewReport' })}
          </CheckBox>
        </Stack>
      </RightPanelContent>

      <RightPanelFooter>
        <Stack direction="column" spacing={0.4}>
          {FeatureFlagsManager.instance.isEnabled('SK-7544-report-payload-download') && (
            <Button
              color="secondary"
              disabled={!canExport}
              leftIcon={{ icon: 'download' }}
              variant="contained"
              width="100%"
              onClick={() => void downloadPayload()}
            >
              {t('cadDrawingReport.export.downloadPayload', { ns: 'skyviewReport' })}
            </Button>
          )}
          <Button
            color="primary"
            disabled={!canExport}
            loading={isExporting}
            variant="contained"
            width="100%"
            onClick={() => void exportReport()}
          >
            {t('exportButton', { ns: 'skyviewReport' })}
          </Button>
        </Stack>
      </RightPanelFooter>

      {isCameraView && page && (
        <CameraViewCrop
          aspectRatio={(() => {
            const mapArea = getMapAreaMm(page.layout, page.size, page.orientation);
            return mapArea.width / mapArea.height;
          })()}
        />
      )}

      {showExportDialog && (
        <CadDrawingReportExportDialog onClose={() => setShowExportDialog(false)} />
      )}
    </>
  );
};

type PageSectionProps = {
  page: PageSettings;
  /** The frame of a drawing to scale; without it, the view has no scale or rotation. */
  frame: ReportFrameState | undefined;
  /** The 3D view, whose scale is shown disabled. */
  isCameraView: boolean;
  tool: ReportTool | undefined;
  unit: LinearUnit;
  onPageChange: (values: Partial<PageSettings>) => void;
};

const PageSection = ({ page, frame, isCameraView, tool, unit, onPageChange }: PageSectionProps) => {
  const { t } = useTranslation();
  const presetScales = getPresetScales(unit);
  const [customScaleSelected, setCustomScaleSelected] = useState(false);
  const isCustomScale = customScaleSelected || !presetScales.includes(page.scale);

  return (
    <Stack spacing={1}>
      {frame && (
        <InfoBox color="blue">
          {t('cadDrawingReport.page.frameInfo', { ns: 'skyviewReport' })}
        </InfoBox>
      )}

      <LabelledContainer text={t('cadDrawingReport.page.paperSize', { ns: 'skyviewReport' })}>
        <ToggleGroup<PaperSize>
          items={paperSizes.map((size) => ({ value: size, label: size }))}
          value={page.size}
          onChange={(size) => onPageChange({ size })}
        />
      </LabelledContainer>

      <LabelledContainer text={t('cadDrawingReport.page.orientation', { ns: 'skyviewReport' })}>
        <ToggleGroup<PageOrientation>
          items={[
            {
              value: 'portrait',
              label: t('cadDrawingReport.page.portrait', { ns: 'skyviewReport' }),
            },
            {
              value: 'landscape',
              label: t('cadDrawingReport.page.landscape', { ns: 'skyviewReport' }),
            },
          ]}
          value={page.orientation}
          onChange={(orientation) => onPageChange({ orientation })}
        />
      </LabelledContainer>

      {(frame || isCameraView) && (
        <LabelledContainer text={t('cadDrawingReport.page.scale', { ns: 'skyviewReport' })}>
          {(getId) => (
            <Stack spacing={0.25}>
              <Select
                disabled={!frame}
                id={getId()}
                options={[
                  ...presetScales.map((scale) => ({
                    id: String(scale),
                    name: formatScale(scale, unit),
                  })),
                  {
                    id: CUSTOM_SCALE,
                    name: t('cadDrawingReport.page.customScale', { ns: 'skyviewReport' }),
                  },
                ]}
                value={isCustomScale ? CUSTOM_SCALE : String(page.scale)}
                onChange={(e) => {
                  const value = e.target.value;
                  setCustomScaleSelected(value === CUSTOM_SCALE);
                  if (value !== CUSTOM_SCALE) {
                    onPageChange({ scale: Number(value) });
                  }
                }}
              />
              {!frame && (
                <Hint>
                  {t('cadDrawingReport.page.scaleDisabledIn3dView', { ns: 'skyviewReport' })}
                </Hint>
              )}
            </Stack>
          )}
        </LabelledContainer>
      )}

      {frame && (
        <>
          {isCustomScale && (
            <LabelledContainer
              text={
                unit === 'ft'
                  ? t('cadDrawingReport.page.customScaleFeet', { ns: 'skyviewReport' })
                  : t('cadDrawingReport.page.customScaleMeters', { ns: 'skyviewReport' })
              }
            >
              {(getId) => (
                <NumberField
                  id={getId()}
                  min={1}
                  value={scaleToInput(page.scale, unit)}
                  onCommit={(value) => {
                    if (value > 0) {
                      onPageChange({ scale: scaleFromInput(value, unit) });
                    }
                  }}
                />
              )}
            </LabelledContainer>
          )}

          <LabelledContainer text={t('cadDrawingReport.page.rotation', { ns: 'skyviewReport' })}>
            {(getId) => (
              <NumberField
                id={getId()}
                value={Math.round(radiansToDegrees(frame.rotation) * 10) / 10}
                onCommit={(degrees) =>
                  tool?.setFrame({
                    ...frame,
                    rotation: degreesToRadians(((degrees % 360) + 360) % 360),
                  })
                }
              />
            )}
          </LabelledContainer>
        </>
      )}
    </Stack>
  );
};

type NumberFieldProps = {
  id: string;
  value: number;
  min?: number;
  onCommit: (value: number) => void;
};

/** How long after the last key a typed number is taken. */
const NUMBER_FIELD_DELAY_MS = 500;

/**
 * A number field that takes its value a moment after typing stops, when it loses focus or when
 * Enter is pressed, so the frame doesn't change with every key. What is typed stays shown until
 * the field loses focus, even if the value is then shown otherwise, as an angle of -10 as 350.
 */
const NumberField = ({ id, value, min, onCommit }: NumberFieldProps) => {
  const [draft, setDraft] = useState<string>();
  const timeout = useRef<ReturnType<typeof setTimeout>>();
  const committed = useRef<number>();
  const onCommitRef = useRef(onCommit);
  onCommitRef.current = onCommit;

  useEffect(() => () => clearTimeout(timeout.current), []);

  const commit = (text: string | undefined) => {
    clearTimeout(timeout.current);
    const parsed = Number(text);
    if (
      text !== undefined &&
      text.trim() !== '' &&
      Number.isFinite(parsed) &&
      parsed !== committed.current
    ) {
      committed.current = parsed;
      onCommitRef.current(parsed);
    }
  };

  const finish = () => {
    commit(draft);
    committed.current = undefined;
    setDraft(undefined);
  };

  return (
    <TextBox
      id={id}
      min={min}
      type="number"
      value={draft ?? String(value)}
      onBlur={finish}
      onChange={(e) => {
        const text = e.target.value;
        setDraft(text);
        clearTimeout(timeout.current);
        timeout.current = setTimeout(() => commit(text), NUMBER_FIELD_DELAY_MS);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          finish();
        }
      }}
    />
  );
};

function isSameSize(frame: ReportFrameState, size: { width: number; height: number }) {
  return (
    Math.abs(frame.width - size.width) <= SIZE_TOLERANCE * size.width &&
    Math.abs(frame.height - size.height) <= SIZE_TOLERANCE * size.height
  );
}

const Hint = styled.span`
  color: ${(props) => props.theme.color.gray.dark};
  font-size: 0.9em;
`;
